import { useState } from "react";

interface IslandCluster {
  island: string;
  icon: string;
  nearestHub: string;
  hubRoute: string;
  regions: {
    name: string;
    communities: string[];
    localContext: string;
  }[];
}

const islandClusters: IslandCluster[] = [
  {
    island: "Oahu",
    icon: "ri-building-2-line",
    nearestHub: "Honolulu",
    hubRoute: "/cities/honolulu",
    regions: [
      {
        name: "North Shore",
        communities: ["Sunset Beach", "Haleiwa", "Waialua", "Pupukea", "Kahuku", "Laie", "Hauula", "Punaluu"],
        localContext: "North Shore residents typically use the Honolulu hub for document processing. Photos and shipping available at Haleiwa and Laie locations. Allow extra time for the Kamehameha Highway drive when dropping off documents.",
      },
      {
        name: "Windward Coast",
        communities: ["Kailua", "Kaneohe", "Waimanalo", "Kaaawa", "Kahaluu", "Ahuimanu"],
        localContext: "Kailua and Kaneohe have full-service shipping and photo centers. The Pali Highway tunnel connects Windward Oahu to Honolulu in under 20 minutes for document handoff if needed.",
      },
      {
        name: "Leeward Oahu",
        communities: ["Kapolei", "Waipahu", "Pearl City", "Ewa Beach", "Nanakuli", "Waianae", "Maili", "Makakilo"],
        localContext: "Leeward communities are well-served by the Kapolei and Waipahu hubs. H-1 Freeway access makes courier pickup and express shipping to the mainland straightforward from this side of the island.",
      },
      {
        name: "Central Oahu",
        communities: ["Mililani", "Wahiawa", "Schofield", "Waipio", "Royal Kunia"],
        localContext: "Central Oahu sits at the crossroads of the H-1 and H-2 freeways, making it a convenient midpoint for residents heading to either Honolulu or Kapolei for passport services.",
      },
      {
        name: "Honolulu Metro",
        communities: ["Waikiki", "Ala Moana", "Downtown", "Kakaako", "Manoa", "Palolo", "Nuuanu", "Kalihi", "Salt Lake", "Moanalua", "Aiea", "Halawa"],
        localContext: "Honolulu has the highest concentration of passport photo locations, shipping centers, and the only same-day courier drop-off option on Oahu. Waikiki and Downtown residents have walkable access to multiple service points.",
      },
      {
        name: "East Oahu",
        communities: ["Hawaii Kai", "Niu Valley", "Aina Haina", "Kahala", "Kaimuki"],
        localContext: "East Oahu residents are a short drive from the Honolulu hub via Kalanianaole Highway. Koko Marina and Kahala Mall areas have photo and shipping services within minutes.",
      },
    ],
  },
  {
    island: "Maui",
    icon: "ri-sun-line",
    nearestHub: "Kahului",
    hubRoute: "/cities/kahului",
    regions: [
      {
        name: "West Maui",
        communities: ["Lahaina", "Kaanapali", "Napili", "Kapalua", "Honokowai", "Kahana"],
        localContext: "West Maui travelers use the Kahului hub for expedited processing. Honoapiilani Highway connects the resort areas to Kahului Airport in 30–45 minutes. Couriers coordinate pickup from Kaanapali and Lahaina hotels.",
      },
      {
        name: "South Maui",
        communities: ["Kihei", "Wailea", "Makena", "Maalaea"],
        localContext: "South Maui is the closest resort area to Kahului Airport, about 15–20 minutes via Piilani Highway. Kihei and Wailea have multiple FedEx and UPS locations for overnight document shipping.",
      },
      {
        name: "Central & Upcountry Maui",
        communities: ["Wailuku", "Waikapu", "Pukalani", "Makawao", "Kula", "Haiku", "Paia"],
        localContext: "Wailuku and Kahului are the processing heart of Maui. Upcountry residents in Kula and Makawao enjoy cooler temperatures but need to plan the 10–15 minute drive down to Kahului for shipping cutoff times.",
      },
      {
        name: "East Maui (Road to Hana)",
        communities: ["Hana", "Keanae"],
        localContext: "Hana is remote — the 52-mile Road to Hana takes 2+ hours one way. Hana residents should mail documents from the Hana post office or schedule a courier pickup with advance notice. Plan for limited cell service along the route.",
      },
    ],
  },
  {
    island: "Big Island",
    icon: "ri-fire-line",
    nearestHub: "Kailua-Kona",
    hubRoute: "/cities/kailua-kona",
    regions: [
      {
        name: "West Hawaii (Kona Coast)",
        communities: ["Kailua-Kona", "Kealakekua", "Captain Cook", "Holualoa", "Waikoloa", "Waimea"],
        localContext: "Kona International Airport (KOA) is the primary departure point for West Hawaii. Kailua-Kona has the most shipping and photo options on the Big Island's west side. Waikoloa resort guests can arrange courier pickup from their hotel.",
      },
      {
        name: "East Hawaii (Hilo District)",
        communities: ["Hilo", "Keaau", "Mountain View", "Kurtistown", "Pahoa", "Volcano"],
        localContext: "Hilo has its own dedicated passport page and several shipping locations. The 90-minute Saddle Road drive connects Hilo to Kona if you need to reach the Kona hub. Volcano Village is about 30 minutes from Hilo.",
      },
      {
        name: "North Hawaii",
        communities: ["Waimea", "Honokaa", "Hawi", "Kapaau"],
        localContext: "North Hawaii is ranch country and coffee territory. Waimea is the largest town with basic shipping access. Residents typically drive to either Kona (45 minutes) or Hilo (1 hour) for full passport services.",
      },
      {
        name: "South Hawaii",
        communities: ["Naalehu", "Pahala", "Ocean View"],
        localContext: "South Point and the Ka'u district are the most remote on the Big Island. The nearest full-service hub is Kailua-Kona, about 1.5 hours north. Ocean View residents should use Priority Mail Express from the local post office.",
      },
    ],
  },
  {
    island: "Kauai",
    icon: "ri-plant-line",
    nearestHub: "Lihue",
    hubRoute: "/cities/lihue",
    regions: [
      {
        name: "East Kauai",
        communities: ["Kapaa", "Wailua", "Anahola", "Lihue"],
        localContext: "East Kauai is the most populated side of the island. Lihue Airport (LIH) is the international gateway. Kapaa and Lihue have the best shipping and photo options on Kauai.",
      },
      {
        name: "North Kauai",
        communities: ["Hanalei", "Princeville", "Kilauea"],
        localContext: "The North Shore is stunning but remote — the Kuhio Highway past Hanalei can close during heavy rains. North Shore residents typically use Kapaa or Lihue as their passport service hub, about 30–40 minutes south.",
      },
      {
        name: "South Kauai",
        communities: ["Koloa", "Poipu", "Lawai", "Kalaheo"],
        localContext: "Poipu and Koloa are close to Lihue (15 minutes) and have reliable shipping access. The South Shore is the driest and sunniest part of Kauai, making it a popular base for travelers needing quick passport turnaround.",
      },
      {
        name: "West Kauai",
        communities: ["Waimea", "Kekaha", "Hanapepe", "Eleele"],
        localContext: "West Kauai is the least populated side of the island. Waimea is the main town. The nearest full-service hub is Lihue, about 45 minutes east via Kaumualii Highway. The west side has limited weekend shipping options.",
      },
    ],
  },
  {
    island: "Molokai & Lanai",
    icon: "ri-map-2-line",
    nearestHub: "Kahului (Maui)",
    hubRoute: "/cities/kahului",
    regions: [
      {
        name: "Molokai",
        communities: ["Kaunakakai", "Kualapuu", "Ho'olehua", "Maunaloa"],
        localContext: "Molokai has no dedicated passport services on-island. Residents typically fly to Maui (15-minute flight from MKK to OGG) or mail documents via Priority Mail Express from the Kaunakakai post office. Plan for inter-island ferry or Mokulele Airlines schedules.",
      },
      {
        name: "Lanai",
        communities: ["Lanai City"],
        localContext: "Lanai is the smallest inhabited island with only one real town. There are no passport photo or shipping centers on Lanai. Residents must ferry or fly to Maui (Lanai to Maui is 45 minutes by ferry) to access full passport services.",
      },
    ],
  },
];

export default function CommunitiesWeServe() {
  const [openIsland, setOpenIsland] = useState<string>("Oahu");

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Island Coverage
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Communities We Serve Across Hawaii
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From tiny plantation towns to resort coastlines, Fast Passport Center covers every
            corner of the Hawaiian Islands. Find your community and the nearest service hub.
          </p>
        </div>

        {/* Island tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {islandClusters.map((cluster) => {
            const isActive = openIsland === cluster.island;
            return (
              <button
                key={cluster.island}
                onClick={() => setOpenIsland(cluster.island)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  isActive
                    ? "bg-[#1A365D] text-white"
                    : "bg-[#F7F8FA] text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200"
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cluster.icon} text-sm`}></i>
                </span>
                {cluster.island}
              </button>
            );
          })}
        </div>

        {/* Active island content */}
        {islandClusters.map((cluster) => {
          if (cluster.island !== openIsland) return null;
          return (
            <div key={cluster.island} className="animate-fade-in">
              {/* Hub banner */}
              <div className="bg-[#0F2240] rounded-lg p-6 md:p-8 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-white text-lg mb-1">
                    Primary Service Hub: {cluster.nearestHub}
                  </h3>
                  <p className="text-white/60 text-sm">
                    All {cluster.island} communities are served through the{" "}
                    {cluster.nearestHub} location with courier pickup and overnight shipping
                    available.
                  </p>
                </div>
                <a
                  href={cluster.hubRoute}
                  className="inline-flex items-center gap-1.5 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Visit {cluster.nearestHub} Page
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              {/* Region grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {cluster.regions.map((region, i) => (
                  <div
                    key={i}
                    className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all"
                  >
                    <h4 className="font-bold text-[#1A365D] text-base mb-3">
                      {region.name}
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {region.communities.map((comm, j) => (
                        <a
                          key={j}
                          href={cluster.hubRoute}
                          className="inline-flex items-center bg-white border border-gray-200 rounded-full px-3 py-1 text-xs text-[#1A365D] hover:border-[#F6C344] hover:text-[#F6C344] transition-all whitespace-nowrap"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center mr-1">
                            <i className="ri-map-pin-line text-[10px]"></i>
                          </span>
                          {comm}
                        </a>
                      ))}
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {region.localContext}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Bottom note */}
        <div className="mt-10 bg-[#FEF9E7] border border-[#F6C344]/20 rounded-lg p-5 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-[#F6C344] text-lg"></i>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#1A365D]">Do not see your town?</strong>{" "}
            We serve every address in Hawaii. If your community is not listed above, the nearest
            hub still covers you — just call{" "}
            <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">
              877-253-0084
            </a>{" "}
            and we will route your application to the closest processing center.
          </p>
        </div>
      </div>
    </section>
  );
}